// GoalFormModal.jsx — create or edit a goal, guided by the SMART check, with a
// live preview of the card. Requestor is whoever created the goal and is not
// editable: it decides who can see the goal.
import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { METRICS, TEMPLATES, ICONS, isoDay, todayIso, daysBetween, progressOf, paceOf } from './goalModel';
import { GoalIcon } from './goalBits';
import { GoalCard, SmartList, scopeLabelFor } from './GoalsView';

const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return isoDay(d); };

function initialDraft(goal, currentUserId) {
  if (goal) {
    return {
      name: goal.name || '', summary: goal.summary || '', detail: goal.detail || '', why: goal.why || '',
      icon: goal.icon || METRICS[goal.metric]?.icon || 'flag', priority: goal.priority || 'MEDIUM',
      metric: goal.metric || '', target: goal.target || '', scope: goal.scope || 'book', current: goal.current || 0,
      start_date: (goal.start_date || goal.created_at || todayIso()).slice(0, 10),
      due_date: (goal.due_date || '').slice(0, 10), assignee: (goal.assignees || [])[0] || '', checklist: [],
    };
  }
  return {
    name: '', summary: '', detail: '', why: '', icon: 'book', priority: 'MEDIUM', metric: 'words', target: 2000,
    scope: 'book', current: 0, start_date: todayIso(), due_date: plusDays(30), assignee: currentUserId || '', checklist: [],
  };
}

export default function GoalFormModal({ open, goal, api, live, chapters, currentUserId, onClose, onSaved }) {
  const [d, setD] = useState(() => initialDraft(goal, currentUserId));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState('');
  const editing = !!goal;

  useEffect(() => { if (open) { setD(initialDraft(goal, currentUserId)); setErrors({}); setNewItem(''); } }, [open, goal, currentUserId]);
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [open, saving, onClose]);

  const set = (k) => (e) => setD((x) => ({ ...x, [k]: e.target.value }));
  const setMetric = (metric, template) => setD((x) => ({
    ...x,
    metric,
    icon: METRICS[metric]?.icon || x.icon,
    target: editing ? x.target : (template?.target ?? x.target),
    checklist: metric === 'tasks' && !x.checklist.length ? ['First step'] : x.checklist,
    ...(template && (!x.name || TEMPLATES.some((t) => t.title === x.name)) ? { name: template.title, summary: template.summary } : {}),
  }));

  const team = api.isAuthor ? api.team : api.team.filter((u) => String(u.id) === String(currentUserId));
  const isTasks = d.metric === 'tasks';
  const manual = d.metric && !METRICS[d.metric]?.auto;

  // The goal as it would be saved, for the preview card and the SMART check.
  const preview = useMemo(() => {
    const target = isTasks ? d.checklist.length : Number(d.target) || 0;
    const cur = d.metric === 'words' ? null : manual ? Number(d.current) || 0 : 0;
    return {
      _id: 'preview', name: d.name || 'Untitled goal', summary: d.summary, why: d.why, icon: d.icon,
      metric: d.metric || null, scope: d.scope, due_date: d.due_date, start_date: d.start_date, created_at: d.start_date,
      priority: d.priority, status: goal?.status || 'unstarted', assignees: d.assignee ? [d.assignee] : [],
      assignee_usernames: d.assignee ? [api.team.find((u) => String(u.id) === String(d.assignee))?.username] : [],
      progress: d.metric ? { current: cur, target, unit: METRICS[d.metric].unit } : null, history: goal?.history || [],
    };
  }, [d, isTasks, manual, goal, api.team]);
  const prog = progressOf(preview, live);
  const pace = paceOf(preview, prog);

  const save = async () => {
    const e = {};
    if (!d.name.trim()) e.name = 'Give the goal a name.';
    if (d.metric && !isTasks && !(Number(d.target) > 0)) e.target = 'Target must be greater than zero.';
    if (isTasks && !editing && !d.checklist.length) e.tasks = 'Add at least one task.';
    if (!d.due_date || (daysBetween(d.start_date, d.due_date) ?? 0) <= 0) e.due = 'The due date must be after the start date.';
    setErrors(e);
    if (Object.keys(e).length) return;

    const body = {
      name: d.name.trim(), summary: d.summary, detail: d.detail, why: d.why, icon: d.icon,
      priority: d.priority, start_date: d.start_date, due_date: d.due_date, metric: d.metric || null,
      assignees: d.assignee ? [d.assignee] : [],
      ...(d.metric && !isTasks ? { target: Number(d.target) } : {}),
      ...(d.metric === 'words' ? { scope: d.scope || 'book' } : {}),
      ...(manual ? { current: Number(d.current) || 0 } : {}),
      ...(!editing && isTasks ? { checklist: d.checklist } : {}),
    };
    setSaving(true);
    const result = editing ? await api.update(goal._id, body) : await api.create(body);
    setSaving(false);
    if (result) onSaved(editing ? goal._id : result.task_id);
  };

  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="gf-ov" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="gf-modal" role="dialog" aria-modal="true" aria-labelledby="gf-title">
        <div className="gf-mh">
          <div>
            <h3 id="gf-title">{editing ? 'Edit goal' : 'New goal'}</h3>
            <small>Define it once — ReVisionari tracks progress and pace for you</small>
          </div>
          <button type="button" className="gf-x" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="gf-mb">
          <div className="gf-form">
            {!editing && (
              <>
                <span className="gf-fl">Start from <i>— picks the right measure for you</i></span>
                <div className="gf-types">
                  {TEMPLATES.map((t) => (
                    <button key={t.metric} type="button" className={d.metric === t.metric ? 'is-on' : ''} onClick={() => setMetric(t.metric, t)}>
                      <GoalIcon icon={METRICS[t.metric].icon} size={34} />{t.label}
                    </button>
                  ))}
                </div>
              </>
            )}
            <label className="gf-fl" htmlFor="gf-name">Goal name <i>(Specific)</i></label>
            <input id="gf-name" className="gv-inp" value={d.name} onChange={set('name')} placeholder="e.g. Draft the Book" autoFocus />
            {errors.name && <div className="gf-err">{errors.name}</div>}
            <label className="gf-fl" htmlFor="gf-summary">Short description</label>
            <input id="gf-summary" className="gv-inp" value={d.summary} onChange={set('summary')} placeholder="One line shown on the goal card" />

            <span className="gf-fl">Measure it <i>(Measurable)</i></span>
            <div className="gf-f3">
              <select className="gv-inp" value={d.metric} onChange={(e) => setMetric(e.target.value)} aria-label="Measure">
                <option value="">No measure</option>
                {Object.entries(METRICS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
              <input className="gv-inp" type="number" min="1" aria-label="Target"
                value={isTasks ? d.checklist.length || (goal ? (goal.checklist || []).length : 0) : d.target}
                disabled={!d.metric || isTasks} title={isTasks ? 'Set by the number of tasks' : undefined}
                onChange={set('target')} />
              {d.metric === 'words' ? (
                <select className="gv-inp" value={d.scope} onChange={set('scope')} aria-label="Scope">
                  <option value="book">Whole manuscript</option>
                  {chapters.map((c, i) => <option key={c.id} value={c.id}>Chapter {i + 1} · {c.title}</option>)}
                </select>
              ) : (
                <input className="gv-inp" type="number" min="0" aria-label="Already done" title="Already done"
                  value={manual ? d.current : 0} disabled={!manual} onChange={set('current')} />
              )}
            </div>
            {d.metric === 'words' && (
              <div className="gf-hint">Tracked automatically from the manuscript ({(live.total || 0).toLocaleString()} words now)</div>
            )}
            {isTasks && !editing && (
              <>
                <span className="gf-fl">Tasks <i>— each one counts toward the goal</i></span>
                {d.checklist.map((t, i) => (
                  <div className="gv-addrow" key={i}>
                    <input className="gv-inp" value={t} aria-label={`Task ${i + 1}`}
                      onChange={(e) => setD((x) => ({ ...x, checklist: x.checklist.map((v, j) => (j === i ? e.target.value : v)) }))} />
                    <button type="button" className="gv-btn is-sm" aria-label="Remove"
                      onClick={() => setD((x) => ({ ...x, checklist: x.checklist.filter((_, j) => j !== i) }))}>×</button>
                  </div>
                ))}
                <div className="gv-addrow">
                  <input className="gv-inp" value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="Add a task…"
                    onKeyDown={(e) => { if (e.key === 'Enter' && newItem.trim()) { e.preventDefault(); setD((x) => ({ ...x, checklist: [...x.checklist, newItem.trim()] })); setNewItem(''); } }} />
                  <button type="button" className="gv-btn is-sm"
                    onClick={() => { if (newItem.trim()) { setD((x) => ({ ...x, checklist: [...x.checklist, newItem.trim()] })); setNewItem(''); } }}>Add</button>
                </div>
                {errors.tasks && <div className="gf-err">{errors.tasks}</div>}
              </>
            )}
            {isTasks && editing && <div className="gf-hint">Change the checklist from the goal’s Tasks tab.</div>}
            {errors.target && <div className="gf-err">{errors.target}</div>}

            <span className="gf-fl">Timeline <i>(Time-bound)</i></span>
            <div className="gf-f2">
              <label className="gf-sub">Start<input className="gv-inp" type="date" value={d.start_date} onChange={set('start_date')} /></label>
              <label className="gf-sub">Due date<input className="gv-inp" type="date" value={d.due_date} onChange={set('due_date')} /></label>
            </div>
            {errors.due && <div className="gf-err">{errors.due}</div>}

            <label className="gf-fl" htmlFor="gf-why">Why does it matter? <i>(Relevant)</i></label>
            <input id="gf-why" className="gv-inp" value={d.why} onChange={set('why')} placeholder="e.g. Send a full draft to my editor before the holidays" />

            <span className="gf-fl">Priority</span>
            <div className="gf-seg" role="radiogroup" aria-label="Priority">
              {['LOW', 'MEDIUM', 'HIGH'].map((p) => (
                <button key={p} type="button" role="radio" aria-checked={d.priority === p} className={d.priority === p ? 'is-on' : ''}
                  onClick={() => setD((x) => ({ ...x, priority: p }))}>{p[0] + p.slice(1).toLowerCase()}</button>
              ))}
            </div>

            <div className="gf-f2">
              <div>
                <span className="gf-fl">Requestor</span>
                <div className="gf-static">{editing ? goal.requestor_username || '—' : 'You'}</div>
              </div>
              <div>
                <label className="gf-fl" htmlFor="gf-asg">Assigned to</label>
                <select id="gf-asg" className="gv-inp" value={d.assignee} onChange={set('assignee')}>
                  <option value="">Unassigned</option>
                  {team.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
                </select>
              </div>
            </div>

            <label className="gf-fl" htmlFor="gf-detail">Detail <i>— what does “done” look like?</i></label>
            <textarea id="gf-detail" className="gv-inp" rows={3} value={d.detail} onChange={set('detail')} placeholder="Acceptance criteria, notes for collaborators…" />
            {api.error && <div className="gf-err is-on">{api.error}</div>}
          </div>

          <div className="gf-prev">
            <p className="gf-pv-lab">Card preview</p>
            <div className="gf-pv-card"><GoalCard goal={preview} live={live} chapters={chapters} actionsFor={() => []} onOpen={() => {}} /></div>
            <p className="gf-pv-lab">Goal criteria</p>
            <div className="gv-cardbox gf-pv-smart"><SmartList goal={preview} prog={prog} pace={pace} scope={scopeLabelFor(preview, chapters)} /></div>
            <p className="gf-pv-lab">Icon</p>
            <div className="gf-icons">
              {ICONS.map((k) => (
                <button key={k} type="button" className={d.icon === k ? 'is-on' : ''} aria-label={`Icon ${k}`} onClick={() => setD((x) => ({ ...x, icon: k }))}>
                  <GoalIcon icon={k} size={40} />
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="gf-mf">
          <button type="button" className="gv-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="gv-btn is-pri" onClick={save} disabled={saving}>{editing ? 'Update goal' : 'Create goal'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
